// src/components/admin/AdminLayout.jsx
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/layout.css';

import { 
  LayoutDashboard, Layers, List, Pill, Scale, 
  AlertTriangle, Tags, Users, Settings, ClipboardList,
  Power, Menu, Calculator, ChevronRight
} from 'lucide-react';

const NAV_ITEMS = [
  { section: 'Overview',  items: [
    { to: '/admin',            icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
  ]},
  { section: 'Medicines', items: [
    { to: '/admin/categories',    icon: <Layers size={20} />, label: 'Categories' },
    { to: '/admin/subcategories', icon: <List size={20} />, label: 'Subcategories' },
    { to: '/admin/medicines',     icon: <Pill size={20} />, label: 'Medicines' },
    { to: '/admin/dosage-rules',  icon: <Scale size={20} />, label: 'Dosage Rules' },
    { to: '/admin/warnings',      icon: <AlertTriangle size={20} />, label: 'Warnings' },
    { to: '/admin/tags',          icon: <Tags size={20} />, label: 'Tags' },
  ]},
  { section: 'System', items: [
    { to: '/admin/users',    icon: <Users size={20} />, label: 'Users & Roles' },
    { to: '/admin/config',   icon: <Settings size={20} />, label: 'Config' },
    { to: '/admin/logs',     icon: <ClipboardList size={20} />, label: 'Logs' },
  ]},
];

const AdminLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = user?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'AD';

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon"><Pill size={28} /></div>
          <div>
            <div className="sidebar-logo-text">MediDose</div>
            <div className="sidebar-logo-sub">Admin Panel</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map(section => (
            <div key={section.section}>
              <div className="sidebar-section-label">{section.section}</div>
              {section.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/admin'}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}

          {/* Quick link to dose calculator */}
          <div style={{ marginTop: 12 }}>
            <div className="sidebar-section-label">Tools</div>
            <NavLink to="/calculator" className="nav-item" onClick={() => setSidebarOpen(false)}>
              <span className="nav-icon"><Calculator size={20} /></span> Dose Calculator
            </NavLink>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initials}</div>
            <div>
              <div className="sidebar-user-name">{user?.name}</div>
              <div className="sidebar-user-role">{user?.roles?.[0] || 'user'}</div>
            </div>
            <button className="sidebar-logout-btn" onClick={handleLogout} title="Logout">
              <Power size={20} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="main-content">
        <header className="topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu size={24} />
          </button>
          <span className="topbar-title">{title || 'Admin Panel'}</span>
          <div className="topbar-spacer" />
          <span className="topbar-badge">MediDose v1.0</span>
        </header>
        <main className="page-content">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99 }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
